import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { ArrowRight, Diamond, Shield, PiggyBank, Award, Scale, TrendingUp, MessageCircle } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { getStaticVillas, getLiveVillas, getStaticSettings, getLiveSettings } from '../lib/api';
import VillaCard from '../components/VillaCard';

const HomePage = () => {
  const { t, language } = useLanguage();
  const [featuredVillas, setFeaturedVillas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [siteSettings, setSiteSettings] = useState({
    youtube_url: 'https://www.youtube.com/embed/EaaCelGtdIs',
    whatsapp_number: '35795989569'
  });
  const liveDataFetched = useRef(false);

  useEffect(() => {
    loadData();
  }, []);

  // SWR Pattern: Static first, then live API in background
  const loadData = async () => {
    // Step 1: Load static data immediately
    const [staticVillas, staticSettings] = await Promise.all([
      getStaticVillas(),
      getStaticSettings()
    ]);
    
    if (staticVillas && staticVillas.length > 0) {
      const featured = staticVillas.filter(v => v.is_featured);
      setFeaturedVillas(featured.length > 0 ? featured.slice(0, 3) : staticVillas.slice(0, 3));
      setLoading(false);
    }
    
    if (staticSettings) {
      setSiteSettings({
        youtube_url: staticSettings.youtube_url || 'https://www.youtube.com/embed/EaaCelGtdIs',
        whatsapp_number: staticSettings.whatsapp_number || '35795989569'
      });
    }
    
    // Step 2: Fetch live data in background (only once)
    if (!liveDataFetched.current) {
      liveDataFetched.current = true;
      
      const [liveVillas, liveSettings] = await Promise.all([
        getLiveVillas(),
        getLiveSettings()
      ]);
      
      if (liveVillas && liveVillas.length > 0) {
        const featured = liveVillas.filter(v => v.is_featured);
        setFeaturedVillas(featured.length > 0 ? featured.slice(0, 3) : liveVillas.slice(0, 3));
      }
      
      if (liveSettings) {
        setSiteSettings({
          youtube_url: liveSettings.youtube_url || 'https://www.youtube.com/embed/EaaCelGtdIs',
          whatsapp_number: liveSettings.whatsapp_number || '35795989569'
        });
      }
      
      setLoading(false);
    }
  };

  // Convert YouTube watch URL to embed URL
  const getEmbedUrl = (url) => {
    if (!url) return 'https://www.youtube.com/embed/EaaCelGtdIs';
    if (url.includes('/embed/')) return url;
    const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s]+)/);
    if (match) {
      return `https://www.youtube.com/embed/${match[1]}`;
    }
    return url;
  };

  const WHATSAPP_NUMBER = siteSettings.whatsapp_number;

  const openWhatsApp = () => {
    const message = language === 'ru' 
      ? 'Здравствуйте! Меня интересуют виллы на первой линии от моря в Хорватии.'
      : 'Hello! I am interested in first line villas in Croatia.';
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`, '_blank');
  };

  const features = [
    { 
      icon: Diamond, 
      title: t('features.bestOffers'), 
      desc: t('features.bestOffersDesc')
    },
    { 
      icon: Award, 
      title: t('features.quality'), 
      desc: t('features.qualityDesc')
    },
    { 
      icon: PiggyBank, 
      title: t('features.saveMoney'), 
      desc: t('features.saveMoneyDesc')
    },
    { 
      icon: Shield, 
      title: t('features.bestPrice'), 
      desc: t('features.bestPriceDesc')
    },
    { 
      icon: Scale, 
      title: t('features.legalSupport'), 
      desc: t('features.legalSupportDesc')
    },
    { 
      icon: TrendingUp, 
      title: t('features.businessGain'), 
      desc: t('features.businessGainDesc')
    },
  ];

  return (
    <>
      <Helmet>
        <title>{String(t('seo.homeTitle'))}</title>
        <meta name="description" content={String(t('seo.homeDescription'))} />
        <meta name="keywords" content="villa croatia, villa dalmatia, luxury villa, first line sea, trogir villa, primosten, omis, croatia real estate, виллы хорватия, недвижимость далмация" />
        <meta property="og:title" content={String(t('seo.homeTitle'))} />
        <meta property="og:description" content={String(t('seo.homeDescription'))} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://customer-assets.emergentagent.com/job_real-estate-hub-236/artifacts/9mit3c43_IMG_20260313_173331.jpg" />
        <meta property="og:url" content="https://villa.ooo" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="robots" content="index, follow" />
        <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=5" />
        <link rel="canonical" href="https://villa.ooo" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "RealEstateAgent",
            "name": "Villa.ooo",
            "description": String(t('seo.homeDescription')),
            "url": "https://villa.ooo",
            "telephone": `+${WHATSAPP_NUMBER}`,
            "areaServed": ["Dalmatia", "Croatia", "Trogir", "Split", "Primosten", "Omis"],
            "priceRange": "€€€€",
            "address": {
              "@type": "PostalAddress",
              "addressRegion": "Dalmatia",
              "addressCountry": "HR"
            },
            "sameAs": [
              `https://wa.me/${WHATSAPP_NUMBER}`
            ]
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "Villa.ooo",
            "url": "https://villa.ooo",
            "logo": "https://villa.ooo/logo.svg",
            "contactPoint": {
              "@type": "ContactPoint",
              "telephone": `+${WHATSAPP_NUMBER}`,
              "contactType": "sales",
              "availableLanguage": ["Russian", "English", "German", "Croatian"]
            }
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="relative h-screen flex items-end" data-testid="hero-section">
        {/* Background Image - User uploaded image */}
        <div className="absolute inset-0">
          <img
            src="https://customer-assets.emergentagent.com/job_real-estate-hub-236/artifacts/9mit3c43_IMG_20260313_173331.jpg"
            alt={language === 'ru' ? 'Роскошная вилла с бассейном infinity на закате в Хорватии' : 'Luxury villa with infinity pool at sunset in Croatia'}
            className="w-full h-full object-cover"
            loading="eager"
            fetchPriority="high"
          />
          <div className="hero-overlay absolute inset-0" />
        </div>

        {/* Content */}
        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-12 pb-16 md:pb-24">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          >
            <p className="label-style text-gold mb-4">{t('hero.subtitle')}</p>
            <h1 className="heading-hero font-heading text-white mb-6 max-w-4xl">
              {t('hero.title')}
            </h1>
            <p className="text-white/70 text-lg md:text-xl max-w-xl mb-10">
              {t('hero.description')}
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/catalog"
                className="btn-primary px-8 py-4 text-sm tracking-widest uppercase inline-flex items-center justify-center gap-3"
                data-testid="hero-cta-catalog"
              >
                {t('hero.cta')}
                <ArrowRight className="w-4 h-4" />
              </Link>
              <button
                onClick={openWhatsApp}
                className="btn-whatsapp px-8 py-4 text-sm tracking-widest uppercase inline-flex items-center justify-center gap-3 bg-[#25D366] text-white border border-[#25D366] hover:bg-[#128C7E] transition-all"
                data-testid="hero-cta-whatsapp"
              >
                <MessageCircle className="w-5 h-5" />
                {t('hero.ctaSecondary')}
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* About Section */}
      <section className="section-padding" data-testid="about-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Image */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              className="aspect-[4/5] lg:aspect-auto lg:h-[600px] overflow-hidden"
            >
              <img
                src="/about-bg.jpg"
                alt="Seafront villas in Dalmatia, Croatia"
                className="w-full h-full object-cover"
              />
            </motion.div>

            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
            >
              <p className="label-style mb-4">{t('about.label')}</p>
              <h2 className="heading-section font-heading text-night mb-8">
                {t('about.title')}
              </h2>
              <p className="text-text-secondary text-lg leading-relaxed mb-8">
                {t('about.description')}
              </p>
              
              <p className="text-text-secondary leading-relaxed mb-12">
                Clean lines, expansive glazing and elegant open-plan interiors frame uninterrupted sea views. Floor-to-ceiling glass fills each space with natural light, creating a seamless connection between refined living and the beauty of the waterfront.
              </p>

              <button
                onClick={openWhatsApp}
                className="btn-primary px-8 py-4 text-sm tracking-widest uppercase inline-flex items-center gap-3"
                data-testid="about-cta-whatsapp"
              >
                <MessageCircle className="w-5 h-5" />
                {language === 'ru' ? 'Написать в WhatsApp' : 'WhatsApp Us'}
              </button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Grid Section */}
      <section className="section-padding bg-night" data-testid="features-section">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <p className="label-style text-gold mb-4">{t('about.label')}</p>
            <h2 className="heading-section font-heading text-white">{t('about.title')}</h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="p-8 border border-white/10 hover:border-gold/50 transition-colors"
              >
                <feature.icon className="w-10 h-10 text-gold mb-6" />
                <h3 className="font-heading text-xl text-white mb-3">{feature.title}</h3>
                <p className="text-white/60 text-sm leading-relaxed">{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Video Presentation Section */}
      <section className="section-padding bg-bone" data-testid="video-section">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <p className="label-style mb-4">{language === 'ru' ? 'Видеопрезентация' : 'Video Presentation'}</p>
            <h2 className="heading-section font-heading text-night">
              {language === 'ru' ? 'Посмотрите наши виллы' : 'Watch Our Villas'}
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="relative aspect-video max-w-4xl mx-auto"
          >
            <iframe
              src={`${getEmbedUrl(siteSettings.youtube_url)}?rel=0&modestbranding=1&hl=en`}
              title="Luxury Villas in Dalmatia, Croatia"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="absolute inset-0 w-full h-full"
              loading="lazy"
              data-testid="video-iframe"
            />
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="text-center text-text-secondary mt-8 max-w-2xl mx-auto"
          >
            {language === 'ru' 
              ? 'Откройте для себя самые красивые виллы далматинских островов — архитектурные шедевры с панорамным видом на Адриатическое море.'
              : 'Discover the most beautiful villas of the Dalmatian Islands — architectural masterpieces with panoramic views of the Adriatic Sea.'
            }
          </motion.p>
        </div>
      </section>

      {/* Featured Villas Section */}
      <section className="section-padding" data-testid="featured-section">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16"
          >
            <div>
              <p className="label-style mb-4">{t('villas.label')}</p>
              <h2 className="heading-section font-heading text-night">{t('villas.title')}</h2>
            </div>
            <Link
              to="/catalog"
              className="text-gold hover:text-night transition-colors flex items-center gap-2 text-sm tracking-wide"
              data-testid="featured-view-all"
            >
              {t('villas.viewAll')}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[1, 2, 3].map((i) => (
                <div key={i} className="animate-pulse">
                  <div className="aspect-[4/3] bg-gray-200 mb-6" />
                  <div className="h-4 bg-gray-200 w-1/3 mb-3" />
                  <div className="h-8 bg-gray-200 w-2/3 mb-3" />
                  <div className="h-4 bg-gray-200 w-1/2" />
                </div>
              ))}
            </div>
          ) : featuredVillas.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {featuredVillas.map((villa, index) => (
                <VillaCard key={villa.id} villa={villa} index={index} />
              ))}
            </div>
          ) : (
            <p className="text-center text-text-secondary py-16">{t('villas.noVillas')}</p>
          )}
        </div>
      </section>

      {/* Dalmatia Section */}
      <section className="section-padding" data-testid="dalmatia-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <p className="label-style mb-4">{language === 'ru' ? 'Регион' : 'Region'}</p>
              <h2 className="heading-section font-heading text-night mb-8">
                {t('dalmatia.title')}
              </h2>
              <p className="text-text-secondary text-lg leading-relaxed mb-6">
                {t('dalmatia.description')}
              </p>
              <p className="text-text-secondary leading-relaxed">
                {language === 'ru'
                  ? 'Далмация — это регион, расположенный в южной части Хорватии, вдоль Адриатического моря. Побережье включает города Сплит, Задар, Дубровник, Шибеник, Трогир и Омиш. Регион известен своей потрясающей природной красотой, изрезанной береговой линией с сотнями островов, кристально чистыми водами и богатым культурным наследием.'
                  : 'Dalmatia is a region located in the southern part of Croatia, along the Adriatic Sea. The coastline includes cities like Split, Zadar, Dubrovnik, Šibenik, Trogir, and Omiš. The region is known for its stunning natural beauty, rugged coastline with hundreds of islands, crystal-clear waters, and rich cultural heritage.'
                }
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="relative"
            >
              <div className="image-hover aspect-[4/3] rounded-lg overflow-hidden shadow-2xl">
                <img
                  src="https://customer-assets.emergentagent.com/job_real-estate-hub-236/artifacts/u5e2m19e_Luxury%20villa%20Omis%20region%2004.png"
                  alt="Dalmatia coastline at sunset"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
              <div className="absolute -bottom-4 -left-4 bg-gold text-night px-6 py-3 font-heading text-lg">
                Omiš, Croatia
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-night" data-testid="cta-section">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h2 className="heading-section font-heading text-white mb-6">
              {language === 'ru' ? 'Готовы стать владельцем виллы мечты?' : 'Ready to Own Your Dream Villa?'}
            </h2>
            <p className="text-white/60 text-lg max-w-xl mx-auto mb-10">
              {language === 'ru' 
                ? 'Свяжитесь с нами сегодня! Мы ответим на все вопросы и поможем выбрать идеальную виллу.'
                : 'Contact us today! We will answer all your questions and help you choose the perfect villa.'
              }
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={openWhatsApp}
                className="bg-[#25D366] text-white px-10 py-4 text-sm tracking-widest uppercase inline-flex items-center justify-center gap-3 hover:bg-[#128C7E] transition-colors"
                data-testid="cta-whatsapp-btn"
              >
                <MessageCircle className="w-5 h-5" />
                WhatsApp
              </button>
              <Link
                to="/contact"
                className="btn-primary px-10 py-4 text-sm tracking-widest uppercase inline-flex items-center justify-center gap-3"
                data-testid="cta-contact-btn"
              >
                {t('contact.label')}
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Floating WhatsApp Button */}
      <button
        onClick={openWhatsApp}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-[#25D366] rounded-full flex items-center justify-center shadow-lg hover:bg-[#128C7E] transition-colors hover:scale-110"
        data-testid="floating-whatsapp-btn"
        aria-label="WhatsApp"
      >
        <MessageCircle className="w-7 h-7 text-white" />
      </button>
    </>
  );
};

export default HomePage;
